// packages/web-ui/src/components/mention-helper/MentionChip.tsx
//
// Story W4-S6 AC #6 row 7-8 — chip render + click-to-remove.
//
// UX §8.6: 24 px tall, rounded 12 px, kind icon + name + `[×]`. The chip
// embeds the resolved entity id as `data-entity-id` so test selectors and
// the composer's outbound message-builder can recover the reference.

import type { JSX } from 'react';
import type { KbEntityKind } from '@tessa-lean/base/api-schemas/index.js';

const KIND_ICON: Readonly<Record<KbEntityKind, string>> = Object.freeze({
  part: '\u{1F537}',
  'part-usage': '\u{1F539}',
  port: '◆',
  interface: '\u{1F50C}',
  'interface-def': '\u{1F50C}',
  connection: '\u{1F517}',
  'connection-def': '\u{1F517}',
  'value-attribute': 'ƒ',
});

export interface MentionChipProps {
  /** Entity id stamped on the chip — used for test selectors + outbound messages. */
  entityId: string;
  /** SysML kind — drives the leading icon. */
  kind: KbEntityKind;
  /** Display text inside the chip (typically the simple name). */
  label: string;
  /** Full qualified name shown as `title` for hover-disambiguation. */
  qualifiedName?: string;
  /** Click handler for the trailing `[×]`. Optional — read-only chips omit it. */
  onRemove?: () => void;
}

/**
 * Inline mention chip rendered into the composer message preview.
 *
 * The component is intentionally presentation-only — text-insertion logic
 * (textarea / contenteditable) lives in the composer story (W2-S7).
 */
export function MentionChip(props: MentionChipProps): JSX.Element {
  const { entityId, kind, label, qualifiedName, onRemove } = props;
  return (
    <span
      data-testid="mention-chip"
      data-entity-id={entityId}
      data-kind={kind}
      title={qualifiedName ?? label}
      className={[
        // 24 px tall × rounded 12 px (UX §8.6).
        'inline-flex h-6 items-center gap-1 rounded-xl border border-taupe-300 bg-cream-200 px-2 text-[12px]',
        'leading-none align-middle',
      ].join(' ')}
    >
      <span aria-hidden="true" data-testid="mention-chip-icon" className="text-[12px]">
        {KIND_ICON[kind]}
      </span>
      <span data-testid="mention-chip-label" className="font-medium text-taupe-900">
        {label}
      </span>
      {onRemove !== undefined ? (
        <button
          type="button"
          data-testid="mention-chip-remove"
          aria-label={`Remove mention ${label}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onRemove();
          }}
          className={[
            'inline-flex h-4 w-4 items-center justify-center rounded-full',
            'text-[10px] leading-none text-taupe-700 hover:bg-cream-300 hover:text-taupe-900',
          ].join(' ')}
        >
          ×
        </button>
      ) : null}
    </span>
  );
}
